'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { CaretRight } from 'phosphor-icons';
import { useMemo, useState, type JSX } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { CheckboxWithLabel } from '../CheckboxWithLabel';
import type {
  TreeAccordionItemProps,
  TreeAccordionProps,
  TreeNodeData,
} from './TreeAccordion.types';

const TREE_ITEM_BASE_STYLES = c(
  'flex w-full items-center justify-between py-2 px-1 gap-3',
  'transition-colors duration-150',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-blue-hover'
);

const TREE_ITEM_CONTENT_STYLES =
  'flex min-w-0 items-center gap-3 overflow-hidden';

const INDENT_PX = 24;
const LEAF_OFFSET_PX = 28;

function getSelectedStyles(isSelected: boolean): {
  text: string;
  count: string;
} {
  if (isSelected) {
    return {
      text: 'text-base font-medium text-[#141414]',
      count: 'text-grey-500',
    };
  }
  return { text: 'text-base text-grey-800', count: 'text-grey-500' };
}

type TreeItemCountProps = {
  count: number;
  isSelected: boolean;
};

function TreeItemCount({ count, isSelected }: TreeItemCountProps): JSX.Element {
  const styles = getSelectedStyles(isSelected);
  return (
    <span
      className={c(
        'flex shrink-0 items-center justify-start gap-2 overflow-hidden rounded bg-grey-100 px-1 text-xs',
        styles.count
      )}
    >
      {count.toLocaleString()}
    </span>
  );
}

/** Collect all non-disabled IDs under a node (disabled nodes keep their current state during cascade) */
function collectAllIds<T>(node: TreeNodeData<T>): string[] {
  if (node.disabled) return [];
  const ids: string[] = [node.id];
  if (node.children) {
    for (const child of node.children) {
      ids.push(...collectAllIds(child));
    }
  }
  return ids;
}

/** Compute check state for a parent node: true (all selected), 'indeterminate' (some), false (none).
 *  Disabled nodes are excluded from the computation — they don't affect parent state. */
function getCheckState<T>(
  node: TreeNodeData<T>,
  selectedSet: Set<string>
): boolean | 'indeterminate' {
  if (!node.children || node.children.length === 0) {
    return selectedSet.has(node.id);
  }

  const enabledChildren = node.children.filter((child) => !child.disabled);

  // If all children are disabled, derive state from the node itself
  if (enabledChildren.length === 0) {
    return selectedSet.has(node.id);
  }

  const childStates = enabledChildren.map((child) =>
    getCheckState(child, selectedSet)
  );

  const allChecked = childStates.every((s) => s === true);
  const noneChecked = childStates.every((s) => s === false);

  if (allChecked) return true;
  if (noneChecked) return false;
  return 'indeterminate';
}

/** Pre-compute check states for all nodes in a tree to avoid O(N^2) recomputation.
 *  Returns a Map of node id -> check state (true | false | 'indeterminate'). */
function buildCheckStateMap<T>(
  nodes: TreeNodeData<T>[],
  selectedSet: Set<string>
): Map<string, boolean | 'indeterminate'> {
  const map = new Map<string, boolean | 'indeterminate'>();

  function traverse(node: TreeNodeData<T>): void {
    map.set(node.id, getCheckState(node, selectedSet));
    node.children?.forEach(traverse);
  }

  nodes.forEach(traverse);
  return map;
}

function TreeAccordionItem<T = unknown>({
  item,
  level = 0,
  selectionMode = 'single',
  selectedId,
  selectedIds,
  checkStateMap,
  onSelect,
  onSelectMany,
  className,
}: TreeAccordionItemProps<T>): JSX.Element {
  const hasChildren = item.children && item.children.length > 0;
  const isMultiple = selectionMode === 'multiple';

  const selectedSet = useMemo(() => new Set(selectedIds ?? []), [selectedIds]);

  const isSelected = isMultiple
    ? selectedSet.has(item.id)
    : selectedId === item.id;

  // Use precomputed check state from map if available, otherwise compute locally
  const checkState = useMemo(() => {
    if (!isMultiple) return isSelected;
    if (checkStateMap) return checkStateMap.get(item.id) ?? false;
    if (!hasChildren) return selectedSet.has(item.id);
    return getCheckState(item, selectedSet);
  }, [isMultiple, isSelected, hasChildren, item, selectedSet, checkStateMap]);

  const isRootLevel = level === 0;
  const basePadding = level * INDENT_PX + 4;
  const styles = getSelectedStyles(
    isMultiple ? checkState === true : isSelected
  );
  const isDisabled = item.disabled ?? false;

  const showIcon = item.icon && isRootLevel;

  const handleMultiSelectClick = () => {
    if (isDisabled) return;

    if (hasChildren && onSelectMany) {
      const allDescendantIds = collectAllIds(item);
      // If not fully checked → select all; if fully checked → deselect all
      const shouldSelect = checkState !== true;
      onSelectMany(allDescendantIds, shouldSelect);
    } else if (onSelectMany) {
      // For leaf nodes in multi-select mode, use onSelectMany for consistency
      onSelectMany([item.id], !isSelected);
    } else {
      // Fallback to onSelect for backward compatibility
      onSelect?.(item.id);
    }
  };

  if (!hasChildren) {
    const leafPadding = isMultiple
      ? basePadding
      : basePadding + (isRootLevel ? 0 : LEAF_OFFSET_PX);

    if (isMultiple) {
      return (
        <div
          data-slot="tree-accordion-item"
          data-test="tree-accordion-item"
          style={{ paddingLeft: `${leafPadding}px` }}
          className={c('flex items-center', className)}
        >
          {/* Invisible spacer to align with sibling items that have carets */}
          <span className="invisible flex shrink-0 items-center justify-center py-2">
            <CaretRight size={16} />
          </span>
          <CheckboxWithLabel
            id={`tree-${item.id}`}
            checked={isSelected}
            disabled={isDisabled}
            onCheckedChange={handleMultiSelectClick}
            role="treeitem"
            aria-level={level + 1}
            aria-selected={isSelected}
          >
            {showIcon && item.icon}
            <span className="flex-1 truncate">{item.label}</span>
            {item.count !== undefined && (
              <span className="rounded bg-grey-100 px-1 text-xs leading-5 tracking-tight text-grey-700">
                {item.count.toLocaleString()}
              </span>
            )}
          </CheckboxWithLabel>
        </div>
      );
    }

    return (
      <button
        data-slot="tree-accordion-item"
        data-test="tree-accordion-item"
        onClick={() => !isDisabled && onSelect?.(item.id)}
        disabled={isDisabled}
        className={c(
          TREE_ITEM_BASE_STYLES,
          'rounded-lg',
          isSelected && 'bg-grey-100',
          isDisabled && 'cursor-not-allowed opacity-50',
          !isDisabled && 'hover:bg-grey-100',
          className
        )}
        style={{ paddingLeft: `${leafPadding}px` }}
        role="treeitem"
        aria-level={level + 1}
        aria-selected={isSelected}
        aria-disabled={isDisabled}
      >
        <div className={TREE_ITEM_CONTENT_STYLES}>
          {showIcon && item.icon}
          <span className={c('truncate', styles.text)}>{item.label}</span>
        </div>
        {item.count !== undefined && (
          <TreeItemCount count={item.count} isSelected={isSelected} />
        )}
      </button>
    );
  }

  return (
    <AccordionPrimitive.Item
      data-slot="tree-accordion-item"
      data-test="tree-accordion-item"
      value={item.id}
      className={c('flex flex-col', className)}
      disabled={isDisabled}
    >
      <AccordionPrimitive.Header className="flex">
        <div
          className={c(
            'group flex w-full items-center',
            'rounded-lg',
            isSelected && !isMultiple && 'bg-grey-100',
            !isMultiple && isDisabled && 'cursor-not-allowed opacity-50',
            !isMultiple && !isDisabled && 'hover:bg-grey-100'
          )}
          style={{ paddingLeft: `${basePadding}px` }}
          role={isMultiple ? 'treeitem' : undefined}
          aria-level={isMultiple ? level + 1 : undefined}
          aria-selected={isMultiple ? checkState === true : undefined}
          aria-disabled={isMultiple ? isDisabled : undefined}
        >
          <AccordionPrimitive.Trigger
            data-test="tree-accordion-caret"
            disabled={isDisabled}
            className={c(
              'group/caret flex shrink-0 items-center justify-center py-2',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-hover'
            )}
          >
            <CaretRight
              size={16}
              className="shrink-0 text-grey-600 transition-transform duration-200 group-data-[state=open]/caret:rotate-90"
            />
          </AccordionPrimitive.Trigger>

          {isMultiple ? (
            <CheckboxWithLabel
              id={`tree-${item.id}`}
              checked={checkState}
              disabled={isDisabled}
              onCheckedChange={handleMultiSelectClick}
              className="flex-1"
              role="treeitem"
              aria-level={level + 1}
              aria-selected={checkState === true}
            >
              {showIcon && item.icon}
              <span className="flex-1 truncate">{item.label}</span>
              {item.count !== undefined && (
                <span className="rounded bg-grey-100 px-1 text-xs leading-5 tracking-tight text-grey-700">
                  {item.count.toLocaleString()}
                </span>
              )}
            </CheckboxWithLabel>
          ) : (
            <button
              type="button"
              disabled={isDisabled}
              onClick={(e) => {
                e.preventDefault();
                if (!isDisabled) {
                  onSelect?.(item.id);
                }
              }}
              className={c(
                TREE_ITEM_BASE_STYLES,
                'flex min-w-0 flex-1 items-center justify-between gap-2 overflow-hidden',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-hover'
              )}
              role="treeitem"
              aria-level={level + 1}
              aria-selected={isSelected}
              aria-disabled={isDisabled}
            >
              <div className={TREE_ITEM_CONTENT_STYLES}>
                {showIcon && item.icon}
                <span className={c('truncate', styles.text)}>{item.label}</span>
              </div>
              {item.count !== undefined && (
                <TreeItemCount count={item.count} isSelected={isSelected} />
              )}
            </button>
          )}
        </div>
      </AccordionPrimitive.Header>
      <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown">
        <div className="flex flex-col">
          {item.children?.map((child) => (
            <TreeAccordionItem
              key={child.id}
              item={child}
              level={level + 1}
              selectionMode={selectionMode}
              selectedId={selectedId}
              selectedIds={selectedIds}
              checkStateMap={checkStateMap}
              onSelect={onSelect}
              onSelectMany={onSelectMany}
            />
          ))}
        </div>
      </AccordionPrimitive.Content>
    </AccordionPrimitive.Item>
  );
}

function TreeAccordion<T = unknown>({
  items,
  selectionMode = 'single',
  selectedId,
  selectedIds,
  expandedIds: controlledExpandedIds,
  defaultExpandedIds = [],
  onSelect,
  onSelectMany,
  onExpandedChange,
  className,
}: TreeAccordionProps<T>): JSX.Element {
  const [internalExpandedIds, setInternalExpandedIds] =
    useState<string[]>(defaultExpandedIds);

  // Pre-compute check states for all nodes once per render (O(N) instead of O(N^2))
  const checkStateMap = useMemo(() => {
    if (selectionMode !== 'multiple' || !selectedIds) return undefined;
    const selectedSet = new Set(selectedIds);
    return buildCheckStateMap(items, selectedSet);
  }, [selectionMode, selectedIds, items]);

  // Use controlled value if provided, otherwise use internal state
  const isControlled = controlledExpandedIds !== undefined;
  const expandedIds = isControlled
    ? controlledExpandedIds
    : internalExpandedIds;

  function handleExpandedChange(newExpandedIds: string[]): void {
    if (!isControlled) {
      setInternalExpandedIds(newExpandedIds);
    }
    onExpandedChange?.(newExpandedIds);
  }

  return (
    <AccordionPrimitive.Root
      data-slot="tree-accordion"
      type="multiple"
      value={expandedIds}
      onValueChange={handleExpandedChange}
      className={c('flex w-full flex-col overflow-hidden', className)}
      role="tree"
      aria-multiselectable={selectionMode === 'multiple' || undefined}
    >
      {items.map((item) => (
        <TreeAccordionItem
          key={item.id}
          item={item}
          selectionMode={selectionMode}
          selectedId={selectedId}
          selectedIds={selectedIds}
          checkStateMap={checkStateMap}
          onSelect={onSelect}
          onSelectMany={onSelectMany}
        />
      ))}
    </AccordionPrimitive.Root>
  );
}

export { TreeAccordion, TreeAccordionItem };
